import * as Axios from "axios";
import { AxiosInstance } from "axios";
import { User } from "../../domain/user/user";
import { Organization } from "../../domain/organization/organization";
import { Group, GroupMetaData } from "../../domain/group/group";
import {
  convert,
  TenantManagementFilesResources,
  HttpMethod,
  TenantManagementResources,
  TypedTenantManagementResource,
} from "./base";
import { Acl, AddressSetting } from "../../domain/acl";
import { UserEntity } from "../../domain/user/user-entity";
import {
  OrgWithUsingFeatures,
  OrgWithUsingFeaturesType,
} from "../../domain/organization/org-with-using-features";
import { CreateUser } from "../../domain/user/user-form-validation";
import { BannerNoticeType } from "../../domain/notifi-from-graffer/banner-notice";
import { CheckAuthDashboardType } from "../../domain/auth/auth";
import { ProductDocument } from "../../domain/product-docs";
import { GroupBatchImportResult } from "../../domain/group/group-batch-import-result";
import {
  UserBatchImportResult,
  UserBatchUpdateResult,
} from "../../domain/user/user-batch-import-result";
import { OrganizationSessionSetting } from "../../domain/organization/org-session-setting";
import { PwnedPasswordHashResponse } from "../../domain/user/pwned-password";
import {
  BatchChangeAuthMethodResult,
  fetchExportAuthAuditLogsTaskStatusResponse,
  GroupsFilterParams,
  StartExportAuthAuditLogsResponse,
  UsersFilterParams,
} from "./types";
import { OrgPaymentSetting } from "@graffer-inc/cloud-gov-core";

export class TenantManagementClient {
  http: AxiosInstance;

  constructor(axios: AxiosInstance) {
    this.http = axios;
  }

  // users
  login = ({
    slug,
    accountName,
    password,
  }: {
    slug: string;
    accountName: string;
    password: string;
  }) =>
    TenantManagementResources(this)(`/login`, HttpMethod.POST, {
      slug,
      accountName,
      password,
    });
  signup = ({ password, name }: { password: string; name: string }) =>
    TenantManagementResources(this)(`/users/signup`, HttpMethod.POST, {
      password,
      name,
    });

  checkTOTP = ({
    MFAJWT,
    userId,
    TOTP,
  }: {
    MFAJWT: string;
    userId: string;
    TOTP: string;
  }) =>
    TenantManagementResources(this)(`/check-totp`, HttpMethod.POST, {
      mfaJWT: MFAJWT,
      userId: userId,
      oneTimePassword: TOTP,
    });

  changePassword = (arg: { password: string }) =>
    TenantManagementResources(this)(`/change-password`, HttpMethod.PUT, arg);

  checkPwnedPassword = (arg: { hash: string }) =>
    TypedTenantManagementResource<PwnedPasswordHashResponse, object>(this)(
      `/check-pwned-password`,
      HttpMethod.POST,
      arg,
    );

  requestTmpPasswordReset = (arg: { slug: string; accountName: string }) =>
    TenantManagementResources(this)(
      `/tmp-password/request-reset`,
      HttpMethod.POST,
      arg,
    );

  resetTmpPassword = (arg: { token: string }) =>
    TenantManagementResources(this)(
      `/tmp-password/reset`,
      HttpMethod.POST,
      arg,
    );

  samlLogin = (slug: string, to?: string) =>
    TypedTenantManagementResource<
      { redirectUrl: string },
      { slug: string; to?: string }
    >(this)(`/saml/login`, HttpMethod.POST, { slug, to });

  //group
  fetchGroups = (page: number, limit: number, filter?: GroupsFilterParams) =>
    TypedTenantManagementResource<Group[], object>(this)(
      `/groups?page=${page}&limit=${limit}`,
      HttpMethod.GET,
      {
        params: {
          name: filter && filter.name,
          nameMatchMode: filter && filter.nameMatchMode,
          ids: filter && filter.ids,
        },
      },
    );

  /*
    ページネーションに対応してすべてのグループを取得する。この関数は pagination-count ヘッダの値分のグループを fetchGroups を使って取得する。
    もし fetchGroups で取得したレスポンスの pagination-count ヘッダが途中で変化した場合は例外を発生させます

    @param limit 1ページあたりの取得件数(デフォルト: 100)
    @param pageLimit 最大ページ数(デフォルト: 100)
    @param filter 検索条件
    @returns Promise<Group[]> 取得したグループのリスト
  */
  fetchAllGroups = async (
    limit = 100,
    pageLimit = 100,
    filter?: GroupsFilterParams,
  ): Promise<Group[]> => {
    let page = 1;
    let groups: Group[] = [];
    let paginationCount: number | undefined;

    while (page <= pageLimit) {
      const { data, headers } = await this.fetchGroups(page, limit, filter);
      groups = groups.concat(data);

      if (paginationCount === undefined) {
        paginationCount = headers["pagination-count"]
          ? Number(headers["pagination-count"])
          : undefined;
      }

      // pagination-count が存在しない場合はすべて取得したとみなす
      if (paginationCount === undefined) {
        break;
      }

      // pagination-count が変化した場合は例外を発生させる
      if (paginationCount !== Number(headers["pagination-count"])) {
        throw new Error("pagination-count changed");
      }

      // 取得したグループが pagination-count に達した場合は取得を終了する
      if (groups.length >= paginationCount) {
        break;
      }

      page++;
    }

    return groups;
  };

  fetchGroupsByOrganizationId = (
    orgId: string,
    page: number,
    limit: number,
    filter?: { name?: string; ids?: string[] },
  ) =>
    TypedTenantManagementResource<Group[], object>(this)(
      `/groups/${orgId}/organization?page=${page}&limit=${limit}`,
      HttpMethod.GET,
      {
        params: {
          name: filter && filter.name,
          ids: filter && filter.ids,
        },
      },
    );
  createGroup = (group: Group) =>
    TenantManagementResources(this)(`/groups`, HttpMethod.POST, group);
  createGroupByServiceProvider = (group: Group, orgId: string) =>
    TenantManagementResources(this)(
      `/groups/${orgId}/organization`,
      HttpMethod.POST,
      group,
    );
  fetchGroupById = (groupId: string) =>
    TypedTenantManagementResource<Group, object>(this)(
      `/groups/${groupId}`,
      HttpMethod.GET,
    );
  fetchGroupUsersById = (groupId: string, page: number, limit: number) =>
    TenantManagementResources(this)(
      `/groups/${groupId}/users?page=${page}&limit=${limit}`,
      HttpMethod.GET,
    );
  updateGroup = (group: Group) =>
    TenantManagementResources(this)(
      `/groups/${group.id}`,
      HttpMethod.PUT,
      group,
    );
  importGroupsByCsv = (orgId: string, value: string, validateOnly: boolean) =>
    TypedTenantManagementResource<GroupBatchImportResult, object>(this)(
      `/organizations/${orgId}/groups/csv-import`,
      HttpMethod.POST,
      {
        data: value,
        validateOnly,
      },
    );
  updateGroupsByCsv = (orgId: string, value: string, validateOnly: boolean) =>
    TypedTenantManagementResource<GroupBatchImportResult, object>(this)(
      `/organizations/${orgId}/groups/csv-update`,
      HttpMethod.PUT,
      {
        data: value,
        validateOnly,
      },
    );
  downloadGroupsCsv = (orgId: string) =>
    TenantManagementResources(this)(
      `/groups/${orgId}/organization/download-csv`,
      HttpMethod.GET,
      {
        responseType: "blob",
        dataType: "binary",
      },
    );
  fetchGroupsMetadata = (
    page: number,
    limit: number,
    filter?: {
      name?: string;
      ids?: string[];
    },
  ) =>
    TypedTenantManagementResource<GroupMetaData[], object>(this)(
      `/groups-metadata?page=${page}&limit=${limit}`,
      HttpMethod.GET,
      {
        params: {
          name: filter && filter.name,
          ids: filter && filter.ids,
        },
      },
    );

  // users
  fetchUser = (id: string) =>
    TypedTenantManagementResource<User, object>(this)(
      `/users/${id}`,
      HttpMethod.GET,
    ).then(convert(UserEntity.fromJSON));
  fetchUsers = (page: number, limit: number, filter?: UsersFilterParams) =>
    TypedTenantManagementResource<User[], object>(this)(
      `/users`,
      HttpMethod.GET,
      {
        params: {
          page,
          limit,
          ...filter,
        },
      },
    ).then(convert(UserEntity.fromJSONArray));
  createUser = (arg: CreateUser) =>
    TenantManagementResources(this)(`/users`, HttpMethod.POST, arg);
  createUserByServiceProvider = (orgId: string, user: CreateUser) =>
    TenantManagementResources(this)(
      `/users/${orgId}/organization`,
      HttpMethod.POST,
      user,
    );
  updateUser = (user: User) =>
    TenantManagementResources(this)(`/users/${user.id}`, HttpMethod.PUT, user);
  disableUser = (id: string) =>
    TenantManagementResources(this)(`/users/${id}/disable`, HttpMethod.PUT, {});
  enableUser = (id: string) =>
    TenantManagementResources(this)(`/users/${id}/enable`, HttpMethod.PUT, {});
  fetchUsersByOrganizationId = (
    id: string,
    page: number,
    limit: number,
    filter?: {
      q?: string;
      accountNames?: string[];
    },
  ) =>
    TypedTenantManagementResource<User[], any>(this)(
      `/users/${id}/org`,
      HttpMethod.GET,
      {
        params: {
          page,
          limit,
          ...filter,
        },
      },
    ).then(convert(UserEntity.fromJSONArray));
  resetUserEmail = (id: string) =>
    TenantManagementResources(this)(
      `/users/${id}/reset-email`,
      HttpMethod.PUT,
      {},
    );
  issueUserMailToken = (id: string, email: string) =>
    TenantManagementResources(this)(
      `/users/${id}/issue-email-token`,
      HttpMethod.PUT,
      {
        email: email,
      },
    );
  verifyUserMailToken = (id: string, email: string, token: string) =>
    TenantManagementResources(this)(
      `/users/${id}/verify-email-token`,
      HttpMethod.PUT,
      {
        email: email,
        token: token,
      },
    );
  resetPassword = (id: string, doSendInvitation: boolean) =>
    TenantManagementResources(this)(
      `/users/${id}/reset-password`,
      HttpMethod.PUT,
      { doSendInvitation },
    );
  resetLoginLock = (id: string) =>
    TenantManagementResources(this)(
      `/users/${id}/reset-lock`,
      HttpMethod.PUT,
      {},
    );
  disableTOTPByUserMgr = (id: string) =>
    TenantManagementResources(this)(
      `/users/${id}/disable-totp`,
      HttpMethod.PUT,
      {},
    );

  /* 認証方式変更 */
  changeAuthMethodToPassword = (id: string) =>
    TenantManagementResources(this)(
      `/users/${id}/change-auth-method-password`,
      HttpMethod.PUT,
      {},
    );
  changeAuthMethodToSAML = (id: string, externalUserID: string) =>
    TenantManagementResources(this)(
      `/users/${id}/change-auth-method-saml`,
      HttpMethod.PUT,
      { externalUserID },
    );

  batchChangeAuthMethodToPassword = (orgId: string, ids: string[]) =>
    TypedTenantManagementResource<BatchChangeAuthMethodResult, object>(this)(
      `/organizations/${orgId}/users/change-auth-method-password`,
      HttpMethod.PUT,
      { userIds: ids },
    );

  batchChangeAuthMethodToSAML = (
    orgId: string,
    targetUsers: { userId: string; externalUserId: string }[],
  ) =>
    TypedTenantManagementResource<BatchChangeAuthMethodResult, object>(this)(
      `/organizations/${orgId}/users/change-auth-method-saml`,
      HttpMethod.PUT,
      {
        targetUsers: targetUsers,
      },
    );

  /* ユーザー CSV 関連 */
  importUsersByCsv = (
    orgId: string,
    value: string,
    validateOnly: boolean,
    doSendInvitation: boolean,
  ) =>
    TypedTenantManagementResource<UserBatchImportResult, object>(this)(
      `/organizations/${orgId}/users/csv-import`,
      HttpMethod.POST,
      {
        data: value,
        validateOnly,
        doSendInvitation,
      },
    );
  updateUsersByCsv = (orgId: string, value: string, validateOnly: boolean) =>
    TypedTenantManagementResource<UserBatchUpdateResult, object>(this)(
      `/organizations/${orgId}/users/csv-update`,
      HttpMethod.PUT,
      {
        data: value,
        validateOnly,
      },
    );
  downloadUsersCsv = (orgId: string, isOutputUserId: boolean) =>
    TenantManagementResources(this)(
      `/users/${orgId}/organization/download-csv`,
      HttpMethod.GET,
      {
        responseType: "blob",
        dataType: "binary",
        params: {
          isOutputUserId,
        },
      },
    );

  // organizations
  fetchOrganization = (orgId: string) =>
    TenantManagementResources(this)(`/organizations/${orgId}`, HttpMethod.GET);
  fetchOrgWithUsingFeature = (orgId: string) =>
    TypedTenantManagementResource<never, OrgWithUsingFeaturesType>(this)(
      `/organizations/${orgId}/with-using-feature`,
      HttpMethod.GET,
    ).then(convert(OrgWithUsingFeatures.fromJSON));
  fetchOrganizations = () =>
    TenantManagementResources(this)(`/organizations`, HttpMethod.GET);
  createOrganization = (org: Organization) =>
    TenantManagementResources(this)(`/organizations`, HttpMethod.POST, org);
  updateOrganization = (org: Organization) =>
    TenantManagementResources(this)(
      `/organizations/${org.id}`,
      HttpMethod.PUT,
      org,
    );
  fetchOrgPaymentSetting = (orgId: string) =>
    TypedTenantManagementResource<OrgPaymentSetting, object>(this)(
      `/organizations/${orgId}/payment-settings`,
      HttpMethod.GET,
    );
  updateOrgPaymentSetting = (setting: OrgPaymentSetting) =>
    TenantManagementResources(this)(
      `/organizations/${setting.orgId}/payment-settings`,
      HttpMethod.PUT,
      setting,
    );
  deleteOrgPaymentSetting = (orgId: string) =>
    TenantManagementResources(this)(
      `/organizations/${orgId}/payment-settings`,
      HttpMethod.DELETE,
    );

  // acl
  fetchOrganizationACL = (orgId: string) =>
    TypedTenantManagementResource<Acl, any>(this)(
      `/organizations/${orgId}/acls`,
      HttpMethod.GET,
    );
  createOrganizationACL = (orgId: string, ipAddresses: Array<AddressSetting>) =>
    TenantManagementResources(this)(
      `/organizations/${orgId}/acls`,
      HttpMethod.POST,
      {
        ipAddresses: ipAddresses,
      },
    );
  updateOrganizationACL = (
    orgId: string,
    aclId: string,
    ipAddresses: Array<AddressSetting>,
  ) =>
    TenantManagementResources(this)(
      `/organizations/${orgId}/acls/${aclId}`,
      HttpMethod.PUT,
      {
        ipAddresses: ipAddresses,
      },
    );

  checkAuthDashboard = () =>
    TypedTenantManagementResource<CheckAuthDashboardType, any>(this)(
      `/check-auth-dashboard`,
      HttpMethod.POST,
      {},
    );
  // MFA
  fetchMFAStatus = () =>
    TenantManagementResources(this)(`/mfa-status`, HttpMethod.GET, {});
  initTOTP = () =>
    TenantManagementResources(this)(`/init-totp`, HttpMethod.PUT, {});
  enableTOTP = (TOTP: string) =>
    TenantManagementResources(this)(`/enable-totp`, HttpMethod.POST, { TOTP });
  disableTOTP = () =>
    TenantManagementResources(this)(`/disable-totp`, HttpMethod.POST, {});
  checkOrgUserPassword = (password: string) =>
    TenantManagementResources(this)(
      `/auth-org-user-with-password`,
      HttpMethod.POST,
      {
        password,
      },
    );

  fetchAuditLogs = (filter: { from?: Date; to?: Date; page: number }) =>
    TenantManagementResources(this)(`/audit-logs`, HttpMethod.GET, {
      params: {
        ...filter,
      },
    });

  startExportAuthAuditLogs = (filter: { from?: Date; to?: Date }) =>
    TypedTenantManagementResource<StartExportAuthAuditLogsResponse, object>(
      this,
    )(`/audit-logs/batch/export/auth-audit-logs`, HttpMethod.POST, {
      ...filter,
    });

  fetchExportAuthAuditLogsTaskStatus = (taskId: string) =>
    TypedTenantManagementResource<
      fetchExportAuthAuditLogsTaskStatusResponse,
      object
    >(this)(
      `/audit-logs/batch/export/auth-audit-logs/status/${taskId}`,
      HttpMethod.GET,
    );

  downloadExportAuthAuditLogs = (taskId: string) =>
    TenantManagementResources(this)(
      `/audit-logs/batch/export/auth-audit-logs/download/${taskId}`,
      HttpMethod.GET,
      {
        responseType: "blob",
        dataType: "binary",
      },
    );

  downloadUserAuditCsv = (filter: {
    from?: Date;
    to?: Date;
    targetUserId?: string;
  }) =>
    TenantManagementResources(this)(
      `/audit-logs/users/download-csv`,
      HttpMethod.GET,
      {
        responseType: "blob",
        dataType: "binary",
        params: {
          ...filter,
        },
      },
    );

  fetchBannerNotice = () =>
    TypedTenantManagementResource<BannerNoticeType, object>(this)(
      `/banner-notice`,
      HttpMethod.GET,
      {},
    );
  saveNotice = (notice: BannerNoticeType) =>
    TypedTenantManagementResource(this)(
      `/banner-notice`,
      HttpMethod.PUT,
      notice,
    );
  deleteNotice = () =>
    TypedTenantManagementResource(this)(
      `/banner-notice`,
      HttpMethod.DELETE,
      {},
    );

  fetchPdtDocs = () =>
    TypedTenantManagementResource<Array<ProductDocument>, object>(this)(
      `/docs/products`,
      HttpMethod.GET,
    );

  uploadProductDocFile = (
    productCode: string,
    fileName: string,
    rawFile: string,
  ) =>
    TypedTenantManagementResource<
      ProductDocument,
      { rawFile: string; fileName: string }
    >(this)(`/docs/products/${productCode}`, HttpMethod.PUT, {
      rawFile: rawFile,
      fileName: fileName,
    });

  downloadProductDocFile = (productCode: string) =>
    TenantManagementFilesResources(this)(
      `/docs/products/${productCode}`,
      HttpMethod.GET,
      {
        responseType: "blob",
        dataType: "binary",
      },
    );

  // org SAML setting
  uploadCertificateFile = (orgId: string, fileName: string, rawFile: string) =>
    TenantManagementFilesResources(this)(
      `/organizations/${orgId}/saml-certificate-file`,
      HttpMethod.POST,
      {
        rawFile: rawFile,
        fileName: fileName,
      },
    );

  downloadSAMLSPMetadata = (orgId: string) =>
    TenantManagementFilesResources(this)(
      `/organizations/${orgId}/saml/metadata`,
      HttpMethod.GET,
      {
        responseType: "blob",
        dataType: "binary",
      },
    );

  fetchOrgIdleSessionTimeoutSetting = (orgId: string) =>
    TypedTenantManagementResource<OrganizationSessionSetting, object>(this)(
      `/organizations/${orgId}/idle-session-timeout`,
      HttpMethod.GET,
    );

  updateOrgIdleSessionTimeoutSetting = (
    orgId: string,
    setting: OrganizationSessionSetting,
  ) =>
    TypedTenantManagementResource<
      OrganizationSessionSetting,
      OrganizationSessionSetting
    >(this)(
      `/organizations/${orgId}/idle-session-timeout`,
      HttpMethod.POST,
      setting,
    );
}

export const setNoCache = (config: Axios.AxiosRequestConfig) => {
  if (!config.params) {
    config.params = {};
  }
  if (
    typeof URLSearchParams === "function" &&
    config.params instanceof URLSearchParams
  ) {
    config.params.append("_", Date.now().toString());
  } else {
    config.params._ = Date.now();
  }
  return config;
};

const client = Axios.default.create({
  headers: {
    Accept: "application/json",
    "Content-Type": "application/json",
  },
});

client.interceptors.request.use(setNoCache);

const apiClient = new TenantManagementClient(client);
export default apiClient;
